import isEmpty from "lodash/isEmpty";

import { diff, endOfDay, fromBackendDate, startOfDay, toBackendDate } from "@tsp-core/datetime/utils";
import { Exception } from "@tsp-core/exception";
import { SortDirection } from "@tsp-core/listing/enums";

import { AccrualModelUnitName, QuantityMapKey } from "@tsp-domains/accrual-models/enums";
import { AccrualModelId, Quantity, QuantityMap } from "@tsp-domains/accrual-models/types";
import {
  parseAccrualModelId,
  parseAccrualModelUnitName,
  transformFromBackendQuantityMap,
} from "@tsp-domains/accrual-models/utils";
import { ContractUuid } from "@tsp-domains/contracts/types";
import { CostAllocationPeriod } from "@tsp-domains/cost-allocation/enums";
import { CustomPaymentUuid } from "@tsp-domains/custom-payments/types";
import { CustomTaskUuid } from "@tsp-domains/custom-tasks/types";
import { CustomWorkUuid } from "@tsp-domains/custom-works/types";
import { DepartmentUuid } from "@tsp-domains/departments/types";
import { DocumentAttachment } from "@tsp-domains/document-attachment/types";
import { ExternalResourceType } from "@tsp-domains/external-resources/enums";
import { ExternalResource } from "@tsp-domains/external-resources/types";
import { parseLink } from "@tsp-domains/external-resources/utils";
import { CurrencyId } from "@tsp-domains/money/enums";
import { Money } from "@tsp-domains/money/types";
import { parseCurrencyId, transformFromStorableMoney, transformToStorableMoney } from "@tsp-domains/money/utils";
import { PartyUuid } from "@tsp-domains/parties/types";
import { PositionUuid } from "@tsp-domains/positions/types";
import { BrandUuid, LocaleCode, PropertyUuid } from "@tsp-domains/projects-portfolio/types";
import { SoulEntityContentType, SoulEntityType } from "@tsp-domains/soul-entities/enums";
import { SoulEntityContentUuid, SoulEntityOrderUuid } from "@tsp-domains/soul-entities/types";
import { WorkSubTypeId, WorkSubTypeUuid, WorkTypeId } from "@tsp-domains/work-types/types";
import { parseWorkSubTypeUuid, parseWorkTypeId } from "@tsp-domains/work-types/utils";

import { WorkItemEntityType, WorkItemStatus } from "../enums";
import {
  WorkItemCommonRecord,
  WorkItemDetailsRecord,
  WorkItemHistoryRecord,
  WorkItemRecord,
  WorkItemSoulEntityContentRecord,
  WorkItemSoulEntityOrderRecord,
  WorkItemUuid,
  WorkItemsRecordsListing,
  WorkItemsRecordsListingFilterValues,
} from "../types";

type WorkItemApi = {
  uuid: WorkItemUuid;
  entityType: WorkItemEntityType;
  entityUuid: string;
  supplierUuid: PartyUuid;
  createdAt: string;
  completedAt: string;
  description: string;
  departmentUuid: DepartmentUuid;
  positionUuid: PositionUuid;
  workTypeId: WorkTypeId;
  workSubTypeUuid: WorkSubTypeUuid;
  currencyId: CurrencyId;
  isExtra: boolean;
  isManual: boolean;
  isStaff: boolean;
  accrualModel: {
    id: AccrualModelId;
    unitName: AccrualModelUnitName;
    defaultQuantityDivider: number;
    isLegacy: boolean;
  };
  rate?: {
    currencyId: CurrencyId;
    fixedPriceStorable: number;
    unitPriceStorable: number;
    rateQuantityDivider: number;
  };
  preTotalAmount?: number;
  lineItem?: {
    uuid: string;
    contractorSignedAt?: string;
    contractorSignedByUuid: PartyUuid;
    quantity?: Quantity;
    quantityMap?: QuantityMap;
    totalAmountStorable?: number;
    embeds: Array<{
      type: string;
      title: string;
      url?: string;
      reach?: string;
    }>;
  };
  statusHistory: Array<{
    date: string;
    status: WorkItemStatus;
    modifiedBy: PartyUuid;
  }>;
  propertyUuid: PropertyUuid | null;
  customTask?: {
    uuid: CustomTaskUuid;
    createdAt: string;
    description?: string;
    quantity?: Quantity;
    quantityMap?: QuantityMap;
    url: string;
    resultUrls: Array<string>;
    approvedByUuid?: PartyUuid;
  };
  customPayment?: {
    uuid: CustomPaymentUuid;
    paidAt: string;
    contract: {
      uuid: ContractUuid;
    };
    totalAmountStorable: number;
    description?: string;
    attachment?: {
      attachment_display_name?: string;
    };
    communicationTaskLink: string;
  };
  customWork?: {
    uuid: CustomWorkUuid;
    paidAt: string;
    teamwoxAttachmentUrl?: string;
    totalAmountStorable: number;
    workSubType: WorkSubTypeId;
    contract: {
      uuid: ContractUuid;
    };
    locale: LocaleCode;
    brandUuid: BrandUuid;
    propertyUuid: PropertyUuid;
    description?: string;
    attachment?: {
      attachment_display_name: string;
    };
    communicationTaskLink: string;
  };
  order?: {
    uuid: SoulEntityOrderUuid;
    propertyUuid: PropertyUuid;
    title: string;
    url: string;
  };
  contentItems?: Array<{
    uuid: SoulEntityContentUuid;
    type: SoulEntityContentType;
    propertyUuid: PropertyUuid;
    title: string;
    url: string;
    thumbnailUrl: string;
  }>;
  costAllocationPeriodType: CostAllocationPeriod | null;
  payoutDocumentNumber?: string | null;
};

function getWorkItemApiRowStorableAmount(item: WorkItemApi): number | null {
  let storable: number | undefined;
  switch (item.entityType) {
    case WorkItemEntityType.CUSTOM_PAYMENT:
      storable = item.customPayment?.totalAmountStorable;
      break;
    case WorkItemEntityType.CUSTOM_WORK:
      storable = item.customWork?.totalAmountStorable;
      break;
    case WorkItemEntityType.CUSTOM_TASK:
      storable = item.lineItem?.totalAmountStorable ?? item.preTotalAmount;
      break;
    default:
      storable = item.lineItem?.totalAmountStorable;
  }
  if (storable === undefined || storable === null || Number.isNaN(Number(storable))) {
    return null;
  }
  return Number(storable);
}

function buildWorkItemsListingMetaFromItems(items: Array<WorkItemApi>): Partial<Record<CurrencyId, Money>> {
  const sums = items.reduce<Partial<Record<CurrencyId, number>>>((acc, item) => {
    const storable = getWorkItemApiRowStorableAmount(item);
    if (storable === null) {
      return acc;
    }

    const currencyId = parseCurrencyId(item.rate?.currencyId ?? item.currencyId);
    acc[currencyId] = (acc[currencyId] ?? 0) + storable;
    return acc;
  }, {});
  return (Object.entries(sums) as Array<[string, number]>).reduce<Partial<Record<CurrencyId, Money>>>(
    (accumulator, [currencyId, amount]) => {
      accumulator[currencyId as unknown as CurrencyId] = transformFromStorableMoney(amount);
      return accumulator;
    },
    {},
  );
}

function getSoulEntityContent(workItem: WorkItemApi): WorkItemSoulEntityContentRecord | null {
  const content = workItem.contentItems?.[0];
  if (content) {
    return {
      type: SoulEntityType.CONTENT,
      contentUuid: content.uuid,
      contentType: content.type,
      propertyUuid: content.propertyUuid,
      link: {
        type: ExternalResourceType.LINK,
        title: content.title,
        href: content.url,
      },
    };
  }

  return null;
}

function getSoulEntityOrder(workItem: WorkItemApi): WorkItemSoulEntityOrderRecord | null {
  if (workItem.order) {
    return {
      type: SoulEntityType.ORDER,
      orderUuid: workItem.order.uuid,
      propertyUuid: workItem.order.propertyUuid,
      link: {
        type: ExternalResourceType.LINK,
        title: workItem.order.title,
        href: workItem.order.url,
      },
      contents:
        workItem.contentItems?.map((contentItem) => {
          return {
            type: SoulEntityType.CONTENT,
            contentUuid: contentItem.uuid,
            contentType: contentItem.type,
            propertyUuid: contentItem.propertyUuid,
            link: {
              type: ExternalResourceType.LINK,
              title: contentItem.title,
              href: contentItem.url,
            },
          };
        }) ?? null,
    };
  }

  return null;
}

export function transformWorkItemApiToWorkItemRecord(workItem: WorkItemApi): WorkItemRecord {
  const history = workItem.statusHistory
    ?.map((item): WorkItemHistoryRecord => {
      return {
        status: item.status,
        modifiedAt: fromBackendDate(item.date),
        modifiedBy: item.modifiedBy,
      };
    })
    .sort((a, b) => {
      return diff(b.modifiedAt, a.modifiedAt);
    });

  const common: WorkItemCommonRecord = {
    uuid: workItem.uuid,
    status: history?.[0]?.status ?? null,
    createdAt: fromBackendDate(workItem.createdAt),
    completedAt: fromBackendDate(workItem.completedAt),
    paidAt:
      history?.findLast((item) => {
        return item.status === WorkItemStatus.PAID;
      })?.modifiedAt ?? null,
    history,
    supplierUuid: workItem.supplierUuid,
    supervisorUuid: null,
    description: "",
    departmentUuid: workItem.departmentUuid,
    positionUuid: workItem.positionUuid,
    workTypeId: parseWorkTypeId(workItem.workTypeId),
    workSubTypeUuid: parseWorkSubTypeUuid(workItem.workSubTypeUuid),
    accrualModelId: parseAccrualModelId(workItem.accrualModel.id),
    accrualModelUnitName: parseAccrualModelUnitName(workItem.accrualModel.unitName),
    accrualModelQuantityDivider: workItem.rate?.rateQuantityDivider ?? workItem.accrualModel.defaultQuantityDivider,
    currencyId: parseCurrencyId(workItem.rate?.currencyId ?? workItem.currencyId),
    fixedPrice: transformFromStorableMoney(workItem.rate?.fixedPriceStorable),
    unitPrice: transformFromStorableMoney(workItem.rate?.unitPriceStorable),
    quantityMap: null,
    total: null,
    isExtra: workItem.isExtra,
    isManual: workItem.isManual,
    isStaff: workItem.isStaff,
    propertyUuid: workItem.propertyUuid,
    costAllocationPeriod: workItem.costAllocationPeriodType,
    payoutDocumentNumber: workItem.payoutDocumentNumber ?? null,
  };

  switch (workItem.entityType) {
    case WorkItemEntityType.CUSTOM_PAYMENT: {
      const { customPayment } = workItem;
      if (!customPayment) {
        throw new Exception(-1, "Unexpected backend response", workItem);
      }

      let attachment: DocumentAttachment | null = null;
      if (customPayment.attachment?.attachment_display_name) {
        attachment = {
          type: "cdn",
          fileName: customPayment.attachment.attachment_display_name,
        };
      }

      const results: Array<ExternalResource> = [];
      if (customPayment.attachment?.attachment_display_name) {
        results.push({
          type: ExternalResourceType.ATTACHMENT,
          name: customPayment.attachment.attachment_display_name,
          entityType: WorkItemEntityType.CUSTOM_PAYMENT,
          entityUuid: customPayment.uuid,
        });
      }

      return {
        ...common,
        entityType: WorkItemEntityType.CUSTOM_PAYMENT,
        entityUuid: customPayment.uuid,
        description: customPayment.description || workItem.description,
        fixedPrice: transformFromStorableMoney(customPayment.totalAmountStorable),
        unitPrice: null,
        quantityMap: { [QuantityMapKey.QUANTITY]: 1 as Quantity },
        total: transformFromStorableMoney(customPayment.totalAmountStorable),
        link: null,
        results,
        contractUuid: customPayment.contract.uuid ?? null,
        attachment,
        communicationLink: workItem.customPayment?.communicationTaskLink ?? null,
      };
    }
    case WorkItemEntityType.CUSTOM_WORK: {
      const { customWork } = workItem;
      if (!customWork) {
        throw new Exception(-1, "Unexpected backend response", workItem);
      }

      let attachment: DocumentAttachment | null = null;
      if (customWork.attachment?.attachment_display_name) {
        attachment = {
          type: "cdn",
          fileName: customWork.attachment.attachment_display_name,
        };
      } else if (customWork.teamwoxAttachmentUrl) {
        attachment = {
          type: "tw",
          link: customWork.teamwoxAttachmentUrl,
        };
      }

      const results: Array<ExternalResource> = [];
      if (customWork.attachment) {
        results.push({
          type: ExternalResourceType.ATTACHMENT,
          name: customWork.attachment.attachment_display_name,
          entityType: WorkItemEntityType.CUSTOM_WORK,
          entityUuid: customWork.uuid,
        });
      }

      return {
        ...common,
        entityType: WorkItemEntityType.CUSTOM_WORK,
        entityUuid: customWork.uuid,
        description: customWork.description || workItem.description,
        fixedPrice: transformFromStorableMoney(customWork.totalAmountStorable),
        unitPrice: null,
        quantityMap: { [QuantityMapKey.QUANTITY]: 1 as Quantity },
        total: transformFromStorableMoney(customWork.totalAmountStorable),
        link: null,
        results,
        contractUuid: customWork.contract?.uuid,
        workSubTypeId: customWork.workSubType,
        localeCode: customWork.locale,
        brandUuid: customWork.brandUuid,
        propertyUuid: customWork.propertyUuid,
        attachment,
        communicationLink: workItem.customWork?.communicationTaskLink ?? null,
      };
    }
    case WorkItemEntityType.CUSTOM_TASK: {
      const { customTask, lineItem } = workItem;
      if (!customTask) {
        throw new Exception(-1, "Unexpected backend response", workItem);
      }

      const soulEntityOrder = getSoulEntityOrder(workItem);
      const soulEntityContent = getSoulEntityContent(workItem);

      const { isLegacy } = workItem.accrualModel;
      const quantity = lineItem?.quantity ?? customTask.quantity ?? null;
      const quantityMap = transformFromBackendQuantityMap(lineItem?.quantityMap ?? customTask.quantityMap) ?? null;

      return {
        ...common,
        entityType: WorkItemEntityType.CUSTOM_TASK,
        entityUuid: customTask.uuid,
        description: customTask.description ?? workItem.description,
        quantityMap: !isEmpty(quantityMap)
          ? quantityMap
          : isLegacy
            ? { [QuantityMapKey.QUANTITY]: quantity }
            : { [QuantityMapKey.EMPTY]: 1 as Quantity },
        total:
          transformFromStorableMoney(lineItem?.totalAmountStorable) ??
          transformFromStorableMoney(workItem.preTotalAmount),
        link: customTask.url ? parseLink(customTask.url) : null,
        results: customTask.resultUrls ? customTask.resultUrls.map(parseLink) : null,
        supervisorUuid: customTask.approvedByUuid ?? null,
        soulEntityOrder,
        soulEntityContent,
      };
    }
    default: {
      const { lineItem } = workItem;
      const soulEntityOrder = getSoulEntityOrder(workItem);
      const soulEntityContent = getSoulEntityContent(workItem);

      const { isLegacy } = workItem.accrualModel;
      const quantity = lineItem?.quantity ?? null;
      const quantityMap = transformFromBackendQuantityMap(lineItem?.quantityMap);

      const results: Array<ExternalResource> = [];
      if (lineItem?.embeds?.length) {
        lineItem.embeds.forEach((embed) => {
          if (embed.type === "link") {
            results.push({
              type: ExternalResourceType.LINK,
              title: embed.title,
              href: embed.url as string,
            });
          } else if (embed.type === "fblink") {
            results.push({
              type: ExternalResourceType.FACEBOOK,
              href: embed.url as string,
              reach: embed.reach as string,
            });
          } else {
            results.push({
              type: ExternalResourceType.TEXT,
              content: embed.title,
            });
          }
        });
      }

      return {
        ...common,
        description: workItem.description,
        entityType: WorkItemEntityType.WORKFLOW_TASK,
        entityUuid: workItem.entityUuid,
        quantityMap: !isEmpty(quantityMap)
          ? quantityMap
          : isLegacy
            ? { [QuantityMapKey.QUANTITY]: quantity }
            : { [QuantityMapKey.EMPTY]: 1 as Quantity },
        total: transformFromStorableMoney(lineItem?.totalAmountStorable),
        link: null,
        results,
        soulEntityOrder,
        soulEntityContent,
      };
    }
  }
}

export type LoadWorkItemsRequest = {
  includePayoutDocumentNumber: true;

  supplierUuid: PartyUuid | null;

  orderUuid: SoulEntityOrderUuid | null;
  contentType: SoulEntityContentType | null;
  contentUuid: SoulEntityContentUuid | null;

  status: Array<number> | null;
  workItemStatus: Array<WorkItemStatus> | null;
  entityType: Array<WorkItemEntityType> | null;

  workTypeId: WorkTypeId | null;
  workSubTypeUuid: Array<WorkSubTypeUuid> | null;
  locale: LocaleCode | null;
  propertyUuid: Array<PropertyUuid> | null;

  approvedByUuid: Array<PartyUuid> | null;
  searchString?: string | null;
  payoutDocumentNumber: string | null;

  totalAmountFilter: {
    operator: "gt" | "lt" | "et";
    amountStorable: number;
  } | null;

  periodFrom: number | null;
  periodTo: number | null;

  orderBy: Array<[string, string]> | null;

  page: number;
  limit: number;

  isStaff: boolean | null;
};

export function transformWorkItemsListingFilterValuesToLoadWorkItemsRequest(
  values: WorkItemsRecordsListingFilterValues,
): LoadWorkItemsRequest {
  const { totalAmountOperator, totalAmount, status, sort, isStaff } = values;

  const totalAmountFilter =
    totalAmountOperator && typeof totalAmount === "number"
      ? {
          operator: totalAmountOperator,
          amountStorable: transformToStorableMoney(totalAmount),
        }
      : null;

  const workItemStatus = status && status.length > 0 ? status : null;

  return {
    includePayoutDocumentNumber: true,

    supplierUuid: values.supplierUuid,

    orderUuid: values.orderUuid,
    contentType: values.contentType,
    contentUuid: values.contentUuid,

    workItemStatus,
    status: null,
    isStaff,
    entityType: values.entityType ? (Array.isArray(values.entityType) ? values.entityType : [values.entityType]) : null,
    workTypeId: values.workTypeId,
    workSubTypeUuid: values.workSubTypeUuids?.length ? values.workSubTypeUuids : null,
    locale: values.localeCode,
    propertyUuid: values.propertyUuid?.length ? values.propertyUuid : null,

    approvedByUuid: values.approvedByUuid ? [values.approvedByUuid] : null,
    searchString: values.search.toString(),
    payoutDocumentNumber: values.payoutDocumentNumber,

    totalAmountFilter,

    limit: values.limit,
    page: values.page,
    periodFrom: values.period[0] ? toBackendDate(startOfDay(values.period[0])) : null,
    periodTo: values.period[1] ? toBackendDate(endOfDay(values.period[1])) : null,
    orderBy: sort ? [[sort.by, sort.direction === SortDirection.ASC ? "asc" : "desc"]] : null,
  };
}

export type LoadWorkItemsResponse = {
  items: Array<WorkItemApi>;
  pagination: {
    count: number;
    limit: number;
    offset: number;
    totalAmount?: number;
  };
};

export function transformLoadWorkItemsResponseToWorkItemsListing(
  response: LoadWorkItemsResponse,
): WorkItemsRecordsListing {
  const meta = buildWorkItemsListingMetaFromItems(response.items);

  const { totalAmount } = response.pagination;
  const resolvedMeta = ((): Partial<Record<CurrencyId, Money>> => {
    if (totalAmount === undefined || isEmpty(meta)) {
      return meta;
    }
    const currencyKeys = Object.keys(meta);
    if (currencyKeys.length === 1) {
      const currencyId = Number(currencyKeys[0]) as CurrencyId;
      return { [currencyId]: transformFromStorableMoney(totalAmount) };
    }
    return meta;
  })();

  return {
    records: response.items.map(transformWorkItemApiToWorkItemRecord),
    pagination: {
      page: response.pagination.offset / response.pagination.limit + 1,
      limit: response.pagination.limit,
      total: response.pagination.count,
    },
    meta: isEmpty(resolvedMeta) ? ({ [CurrencyId.EUR]: 0 } as Record<CurrencyId, Money>) : resolvedMeta,
  };
}

export type LoadWorkItemResponse = {
  workItem: WorkItemApi;
};

export function transformLoadWorkItemResponseToWorkItemDetails(response: LoadWorkItemResponse): WorkItemDetailsRecord {
  return transformWorkItemApiToWorkItemRecord(response.workItem);
}
